import type { PropertyPhoto } from '@/types/propertyProfile'

const p = (file: string, alt: string): PropertyPhoto => ({
  src: `/property-photos/${file}`,
  alt,
})

/** Bundled JPEGs under /public/property-photos — no external CDN at runtime. */
export const PHOTO_SET_MULTIFAMILY: PropertyPhoto[] = [
  p('mf-tower.jpg', 'High-rise residential tower — aerial context'),
  p('mf-lobby.jpg', 'Grand residential lobby with concierge desk'),
  p('mf-pool.jpg', 'Resort-style pool deck and amenity courtyard'),
  p('mf-interior.jpg', 'Modern unit interior with open living space'),
]

export const PHOTO_SET_MULTIFAMILY_MIDRISE: PropertyPhoto[] = [
  p('mf-midrise-street.jpg', 'Mid-rise community street facade'),
  p('mf-pool.jpg', 'Resort-style pool deck and amenity courtyard'),
  p('mf-interior.jpg', 'Clubhouse-style lounge and seating'),
  p('mf-kitchen.jpg', 'Open-concept kitchen in model residence'),
]

export const PHOTO_SET_OFFICE: PropertyPhoto[] = [
  p('office-exterior.jpg', 'Class A office tower — street perspective'),
  p('office-lobby.jpg', 'Corporate lobby with reception and seating'),
  p('office-floor.jpg', 'Open office floor plate with daylight'),
  p('office-meeting.jpg', 'Conference suite and collaboration space'),
]

export const PHOTO_SET_INDUSTRIAL: PropertyPhoto[] = [
  p('industrial-aerial.jpg', 'Aerial view of logistics and distribution campus'),
  p('industrial-loading.jpg', 'Truck court with dock-high loading positions'),
  p('industrial-office.jpg', 'Modern industrial office and reception build-out'),
  p('industrial-warehouse.jpg', 'Interior warehouse with high clear height'),
]

export const PHOTO_SET_LOGISTICS_ALT: PropertyPhoto[] = [
  p('logistics-aerial.jpg', 'Intermodal logistics — aerial container and yard context'),
  p('truck-court.jpg', 'Distribution truck court and trailer staging'),
  p('office-buildout.jpg', 'Industrial office build-out and workspace'),
  p('warehouse-racking.jpg', 'Interior with selective rack and staging aisles'),
]

export const PHOTO_SET_HOSPITALITY: PropertyPhoto[] = [
  p('hotel-deco.jpg', 'Boutique hotel facade and porte-cochère'),
  p('hotel-pool.jpg', 'Rooftop pool deck with skyline views'),
  p('hotel-suite.jpg', 'Renovated guest suite — king room'),
  p('hotel-ballroom.jpg', 'Grand ballroom and event space'),
]

export const PHOTO_SET_RETAIL: PropertyPhoto[] = [
  p('retail-center.jpg', 'Neighborhood retail center — storefront line'),
  p('retail-interior.jpg', 'Inline retail bay and merchandising floor'),
  p('retail-parking.jpg', 'Surface parking and monument signage'),
  p('retail-aerial.jpg', 'Aerial context — pad and access drives'),
]

export const PHOTO_SET_LAB: PropertyPhoto[] = [
  p('lab-exterior.jpg', 'Life science / R&D campus exterior'),
  p('lab-lobby.jpg', 'Research building lobby and circulation'),
  p('lab-suite.jpg', 'Wet lab suite with casework and MEP'),
  p('lab-common.jpg', 'Collaboration commons and write-up space'),
]

export const PHOTO_SET_SENIOR: PropertyPhoto[] = [
  p('senior-exterior.jpg', 'Senior living community — main building'),
  p('senior-dining.jpg', 'Resident dining and bistro'),
  p('senior-lounge.jpg', 'Common lounge and activity space'),
  p('senior-suite.jpg', 'Model assisted-living suite'),
]

export const PHOTO_SET_MIXED_USE: PropertyPhoto[] = [
  p('mixed-tower.jpg', 'Mixed-use tower — retail base and residential'),
  p('mixed-retail.jpg', 'Activated street-level retail frontage'),
  p('mixed-lobby.jpg', 'Residential lobby with dual elevator banks'),
  p('mixed-rooftop.jpg', 'Rooftop terrace and amenity deck'),
]

export const PHOTO_SET_LAND: PropertyPhoto[] = [
  p('land-aerial.jpg', 'Entitled land parcel — aerial overview'),
  p('land-grade.jpg', 'Graded site with utility rough-in'),
  p('land-road.jpg', 'Frontage road and access point'),
  p('land-horizon.jpg', 'Context view toward CBD / growth corridor'),
]

export const PHOTO_SET_LUXURY_MF: PropertyPhoto[] = [
  p('lux-evening-tower.jpg', 'Illuminated high-rise — evening context'),
  p('lux-infinity-pool.jpg', 'Infinity-edge pool with skyline backdrop'),
  p('lux-penthouse.jpg', 'Penthouse living — floor-to-ceiling glass'),
  p('lux-concierge-lobby.jpg', 'Concierge lobby with art and seating'),
]

export function photosForAssetType(type: string): PropertyPhoto[] {
  switch (type) {
    case 'Multifamily':
      return PHOTO_SET_MULTIFAMILY
    case 'Office':
      return PHOTO_SET_OFFICE
    case 'Industrial':
      return PHOTO_SET_INDUSTRIAL
    case 'Hospitality':
      return PHOTO_SET_HOSPITALITY
    case 'Retail':
      return PHOTO_SET_RETAIL
    case 'Lab/R&D':
      return PHOTO_SET_LAB
    case 'Senior Living':
      return PHOTO_SET_SENIOR
    case 'Mixed-Use':
      return PHOTO_SET_MIXED_USE
    case 'Land':
      return PHOTO_SET_LAND
    default:
      return PHOTO_SET_OFFICE
  }
}
